import { useEffect, useRef } from 'react'
import { useApolloClient } from '@apollo/client'
import { useAuthStore } from '@/stores/auth'
import { useRenderJobsStore, type RenderJobStatus } from '@/stores/renderJobs'
import { LIST_DIRECTOR_JOBS } from '@/graphql/editTemplate'

// Statuses that mean a job is still running (terminal = completed/failed). Mirrors
// DirectorJobStatusOptions in rolla-db's schema; keep in sync with renderJobs.ts.
const IN_FLIGHT_STATUSES: RenderJobStatus[] = ['queued', 'processing', 'rendering', 'uploading']

// premiere-export vs video render — see directorVersion in rolla-db (branded-reel-v0 / premiere-export-v0).
const EXPORT_DIRECTOR_VERSION = 'premiere-export-v0'

// Only re-seed jobs created recently. A real render/export finishes in minutes, so anything older is a
// stale/orphaned row (a worker died mid-render and never marked it terminal) — showing those on every
// page load just clutters the widget with jobs that will never complete. Boot recovery (director) retries
// or fails genuine orphans server-side; this cutoff keeps the UI clean meanwhile.
const MAX_REHYDRATE_AGE_MS = 12 * 60 * 60 * 1000 // 12h

interface DirectorJobRow {
  id: string
  status: RenderJobStatus
  percent: number | null
  stage: string | null
  error: string | null
  directorVersion: string | null
  createdAt: string | null
  project: { id: string; name: string | null } | null
}

// The render-jobs store is in-memory, so a full page reload loses track of a render/export that's
// still running (the floating RenderProgressWidget disappears even though the job keeps going on the
// backend). On load — and whenever the selected team changes — this re-seeds the store with that
// team's in-flight jobs so the widget comes back and resumes polling. Best-effort: a failed fetch
// just leaves the widget empty until the next job is queued, and it's retried on the next mount.
export function useRehydrateRenderJobs() {
  const apolloClient = useApolloClient()
  const selectedTeamId = useAuthStore((s) => s.selectedTeamId)
  const addJob = useRenderJobsStore((s) => s.addJob)
  // Per-team guard so this runs once per team, not on every render.
  const rehydratedTeams = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!selectedTeamId || rehydratedTeams.current.has(selectedTeamId)) return
    rehydratedTeams.current.add(selectedTeamId)

    let cancelled = false
    ;(async () => {
      try {
        const { data } = await apolloClient.query({
          query: LIST_DIRECTOR_JOBS,
          variables: { teamId: selectedTeamId, statuses: IN_FLIGHT_STATUSES, limit: 25 },
          fetchPolicy: 'network-only',
        })
        if (cancelled) return

        const rows: DirectorJobRow[] = data?.listDirectorJobs?.rows ?? []
        // Don't duplicate jobs already tracked (e.g. one just queued from the Generate modal).
        const existing = new Set(useRenderJobsStore.getState().jobs.map((j) => j.id))

        for (const row of rows) {
          if (existing.has(row.id)) continue
          // Skip stale/orphaned jobs (older than the cutoff) so the widget doesn't resurrect zombies.
          if (row.createdAt && Date.now() - new Date(row.createdAt).getTime() > MAX_REHYDRATE_AGE_MS) continue
          addJob({
            id: row.id,
            projectName: row.project?.name || 'Untitled project',
            status: row.status,
            percent: row.percent ?? 0,
            stage: row.stage ?? null,
            error: row.error ?? null,
            kind: row.directorVersion === EXPORT_DIRECTOR_VERSION ? 'export' : 'render',
            createdAt: row.createdAt ?? undefined,
          })
        }
      } catch {
        // Allow a retry on the next mount / team switch if this fetch didn't land.
        rehydratedTeams.current.delete(selectedTeamId)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [selectedTeamId, apolloClient, addJob])
}
